import { useCallback, useState } from "react";
import { isZapierUrl, isBlockedUrl } from "./utils";

type AlertType = "confirmation" | "blocked" | null;

interface UseLinkSecurityHandlerProps {
  isPublic: boolean;
  blockedDomains: string[];
}

export function useLinkSecurityHandler({
  isPublic,
  blockedDomains,
}: UseLinkSecurityHandlerProps) {
  const [alertType, setAlertType] = useState<AlertType>(null);
  const [pendingUrl, setPendingUrl] = useState<string>("");

  const handleLinkClick = useCallback(
    (href: string) => {
      if (!isPublic) {
        // Non-public canvas - open directly
        window.open(href, "_blank");
        return;
      }

      // Skip warning for Zapier URLs
      if (isZapierUrl(href)) {
        window.open(href, "_blank");
        return;
      }

      // Show blocked alert for blocked domains
      if (isBlockedUrl(href, blockedDomains)) {
        setPendingUrl(href);
        setAlertType("blocked");
        return;
      }

      // Show confirmation for external URLs
      setPendingUrl(href);
      setAlertType("confirmation");
    },
    [isPublic, blockedDomains],
  );

  const handleConfirmationCancel = useCallback(() => {
    setAlertType(null);
    setPendingUrl("");
  }, []);

  const handleConfirmationContinue = useCallback(() => {
    setAlertType(null);
    window.open(pendingUrl, "_blank");
    setPendingUrl("");
  }, [pendingUrl]);

  const handleBlockedDismiss = useCallback(() => {
    setAlertType(null);
    setPendingUrl("");
  }, []);

  return {
    // State
    alertType,
    // Handlers
    handleLinkClick,
    handleConfirmationCancel,
    handleConfirmationContinue,
    handleBlockedDismiss,
  };
}
